import { Button, Checkbox, ScrollArea, TabsContent } from '@zond/ui';
import { Download, FolderKanban } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from './empty-state';
import { IntegrationItem } from './integration-item';
import { LoadingSkeleton } from './loading-skeleton';
import { getProjectId, getProjectName, isGithub } from './utils';

interface ProjectsTabProps {
  integrationName: string;
  projects: any[];
  projectsLoading: boolean;
  selectedProjects: Set<string>;
  onProjectToggle: (projectId: string) => void;
  onSelectAllProjects: () => void;
  onExportProjects: () => void;
  isExportingProjects: boolean;
}

export function ProjectsTab({
  integrationName,
  projects,
  projectsLoading,
  selectedProjects,
  onProjectToggle,
  onSelectAllProjects,
  onExportProjects,
  isExportingProjects,
}: ProjectsTabProps) {
  const { t } = useTranslation('integrations');
  const isGithubIntegration = isGithub(integrationName);

  return (
    <TabsContent
      value="projects"
      className="mt-0 mx-6 pb-4 max-w-full overflow-x-hidden"
      style={{ maxWidth: '752px', width: '100%' }}
    >
      <div className="flex items-center justify-between mb-4 pt-4">
        <div className="flex items-center gap-2">
          <Checkbox
            id="select-all-projects"
            checked={projects?.length > 0 && selectedProjects.size === projects?.length}
            onCheckedChange={onSelectAllProjects}
          />
          <label
            htmlFor="select-all-projects"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            {t('list.selectAll', { count: selectedProjects.size })}
          </label>
        </div>
        <Button
          size="sm"
          onClick={onExportProjects}
          disabled={selectedProjects.size === 0 || isExportingProjects}
          className="gap-2"
        >
          <Download className="h-4 w-4" />
          {isExportingProjects ? t('projectsTab.exporting') : t('list.exportSelected')}
        </Button>
      </div>

      <ScrollArea
        className="rounded-md border h-[200px] !w-full !max-w-full overflow-x-hidden"
        style={{ maxWidth: '100%', width: '100%' }}
      >
        <div className="p-4 overflow-x-hidden">
          {projectsLoading ? (
            <LoadingSkeleton />
          ) : projects?.length === 0 ? (
            <EmptyState
              icon={FolderKanban}
              title={
                isGithubIntegration
                  ? t('projectsTab.empty.repositoriesTitle')
                  : t('projectsTab.empty.projectsTitle')
              }
              description={
                isGithubIntegration
                  ? t('projectsTab.empty.repositoriesDescription')
                  : t('projectsTab.empty.projectsDescription')
              }
            />
          ) : (
            <div className="space-y-2 overflow-x-hidden">
              {projects?.map((project: any) => {
                const projectId = getProjectId(project);
                const projectName = getProjectName(project);
                const projectDescription = project.description;
                const isSelected = selectedProjects.has(projectId);

                return (
                  <IntegrationItem
                    key={projectId}
                    id={projectId}
                    name={projectName}
                    description={projectDescription}
                    icon={projectName.slice(0, 2).toUpperCase()}
                    isSelected={isSelected}
                    onToggle={onProjectToggle}
                  />
                );
              })}
            </div>
          )}
        </div>
      </ScrollArea>
    </TabsContent>
  );
}
